import type { FC } from 'react';
import { config, menuIcon } from '@/configs';
import defaultSettings from '@/defaultSettings';
import { useLocation } from '@/hooks';
import { isDevelopEnv } from '@/utils';
import ProLayout, {
  getMenuData,
  MenuDataItem,
  SettingDrawer,
  SettingDrawerProps,
} from '@ant-design/pro-layout';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import './index.css';
import type { MenuProps } from 'antd/es/menu';
import { useRecoilState, useRecoilValue } from 'recoil';
import { tokenState, menuInfo, loginInfo } from '../../state';
import Button from 'antd/es/button';

const renderMenuItem = (menus: MenuDataItem[]): MenuDataItem[] =>
  menus.map(({ icon, children, ...item }) => ({
    ...item,
    icon: icon && menuIcon[icon as string],
    children: children && renderMenuItem(children),
  }));

const BasicLayout: FC = props => {
  const [token, setToken] = useRecoilState(tokenState);
  const infoLogin = useRecoilValue(loginInfo);
  const infoMenu = useRecoilValue(menuInfo);

  const location = useLocation();
  const [settings, setSetting] = useState(
    defaultSettings(infoLogin.login) as SettingDrawerProps['settings']
  );
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>(['/']);
  const isDev = isDevelopEnv();
  const { pathname } = location;
  const { breadcrumbMap, menuData } = useMemo(
    () => getMenuData(infoMenu.opciones),
    []
  );

  useEffect(() => {
    const select = breadcrumbMap.get(pathname);
    if (select) {
      setOpenKeys((select as MenuDataItem)['pro_layout_parentKeys']);
      setSelectedKeys([(select as MenuDataItem)['key'] as string]);
    }
  }, [breadcrumbMap, pathname]);

  const menuDataRender = useCallback(
    () => renderMenuItem(menuData),
    [menuData]
  );

  const menuItemRender = useCallback((menuItemProps, defaultDom) => {
    if (menuItemProps.isUrl || !menuItemProps.path) {
      return defaultDom;
    }
    return <Link to={menuItemProps.path}>{defaultDom}</Link>;
  }, []);

  const handleOnOpenChange = useCallback(
    (keys: string[]) => setOpenKeys(keys as string[]),
    []
  );

  const menuProps = useMemo<MenuProps>(() => {
    return {
      selectedKeys,
      openKeys,
      onOpenChange: handleOnOpenChange,
    };
  }, [handleOnOpenChange, openKeys, selectedKeys]);

  return (
    <>
      <ProLayout
        {...settings}
        locale="en-US"
        collapsed
        headerHeight={150}
        fixSiderbar={true}
        title={config.title}
        logo={config.logo}
        rightContentRender={() => {
          if (infoLogin.login) {
            return (
              <Button
                type="primary"
                onClick={() => {
                  setToken(null);
                }}
              >
                Cerrar sesión
              </Button>
            );
          }
        }}
        menuDataRender={menuDataRender}
        menuItemRender={menuItemRender}
        menuProps={menuProps}
      >
        {props.children}
      </ProLayout>

      {isDev && (
        <SettingDrawer
          getContainer={() => document.getElementById('root')}
          settings={settings}
          onSettingChange={setSetting}
        />
      )}
    </>
  );
};

export default BasicLayout;
